import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";

/** Bordered list page shell (Salesforce-style list view). */
export function SfListShell({
  objectLabel,
  viewLabel = "All records",
  metaRight,
  actions,
  toolbar,
  footer,
  children,
}: {
  objectLabel: string;
  viewLabel?: string;
  metaRight?: ReactNode;
  actions?: ReactNode;
  toolbar?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      className="overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]"
      style={{ boxShadow: "var(--shadow-card)" }}
    >
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[var(--color-border)] bg-[var(--color-surface-2)] px-4 py-3 sm:items-center sm:px-5">
        <div className="min-w-0">
          <div className="flex flex-wrap items-baseline gap-2">
            <h2 className="text-base font-semibold tracking-tight text-[var(--color-text)]">
              {objectLabel}
            </h2>
            <span className="text-xs font-medium text-[var(--color-text-muted)]">·</span>
            <span className="text-xs font-medium text-[var(--color-accent)]">{viewLabel}</span>
          </div>
          {metaRight ? (
            <p className="mt-1 text-[11px] text-[var(--color-text-subtle)]">{metaRight}</p>
          ) : null}
        </div>
        {actions ? (
          <div className="flex flex-shrink-0 flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </div>
      {toolbar ? (
        <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2 sm:px-5">
          {toolbar}
        </div>
      ) : null}
      <div className="bg-[var(--color-surface)]">{children}</div>
      {footer ? (
        <div className="border-t border-[var(--color-border)] bg-[var(--color-surface-2)] px-4 py-3 text-xs text-[var(--color-text-muted)] sm:px-5">
          {footer}
        </div>
      ) : null}
    </div>
  );
}

/** Salesforce-style record header + optional highlights under it. */
export function SfRecordHero({
  icon,
  objectLabel,
  recordTitle,
  subtitle,
  actions,
  highlights,
  alert,
}: {
  icon?: ReactNode;
  objectLabel: string;
  recordTitle: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  highlights?: { label: string; value: ReactNode }[];
  alert?: ReactNode;
}) {
  return (
    <div
      className="overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]"
      style={{ boxShadow: "var(--shadow-card)" }}
    >
      <div className="border-b border-[var(--color-border)] px-4 py-4 sm:px-6">
        <div className="flex flex-wrap items-start gap-3">
          {icon ? (
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-[var(--color-accent-soft)] text-[var(--color-accent)]">
              {icon}
            </div>
          ) : null}
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--color-text-subtle)]">
              {objectLabel}
            </p>
            <h1 className="mt-0.5 truncate text-lg font-semibold text-[var(--color-text)]">
              {recordTitle}
            </h1>
            {subtitle ? (
              <div className="mt-1 text-xs text-[var(--color-text-muted)]">{subtitle}</div>
            ) : null}
          </div>
          {actions ? (
            <div className="flex w-full flex-shrink-0 flex-wrap gap-2 sm:w-auto sm:justify-end">{actions}</div>
          ) : null}
        </div>
      </div>
      {highlights && highlights.length > 0 ? (
        <div className="flex flex-wrap gap-x-8 gap-y-2 border-b border-[var(--color-border)] bg-[var(--color-surface-2)] px-4 py-2.5 sm:px-6">
          {highlights.map((h) => (
            <div key={h.label} className="min-w-[120px]">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-subtle)]">
                {h.label}
              </p>
              <div className="mt-0.5 text-sm text-[var(--color-text)]">{h.value}</div>
            </div>
          ))}
        </div>
      ) : null}
      {alert ? <div className="border-b border-[var(--color-border)]">{alert}</div> : null}
    </div>
  );
}

export function SfTabBar({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: string; label: string }[];
  active: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1 border-b border-[var(--color-border)] px-4 sm:px-5">
      {tabs.map((t) => {
        const on = t.id === active;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            className={cn(
              "-mb-px border-b-2 px-3 py-2 text-xs font-medium transition",
              on
                ? "border-[var(--color-accent)] text-[var(--color-accent)]"
                : "border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)]",
            )}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

/** Two-column details grid (label / value). */
export function SfFieldGrid({ rows }: { rows: { label: string; value: ReactNode }[] }) {
  return (
    <dl className="grid grid-cols-1 gap-x-10 gap-y-4 sm:grid-cols-2">
      {rows.map((r) => (
        <div key={r.label} className="border-b border-[var(--color-border)] pb-3 sm:border-0 sm:pb-0">
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-subtle)]">
            {r.label}
          </dt>
          <dd className="mt-1 text-sm text-[var(--color-text)]">{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Main column + optional right sidebar (Lightning-style). */
export function SfSplitDetail({
  tabs,
  tabActive,
  onTabChange,
  main,
  sidebar,
}: {
  tabs?: { id: string; label: string }[];
  tabActive?: string;
  onTabChange?: (id: string) => void;
  main: ReactNode;
  sidebar?: ReactNode;
}) {
  return (
    <div
      className="overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]"
      style={{ boxShadow: "var(--shadow-card)" }}
    >
      {tabs && tabActive != null && onTabChange ? (
        <SfTabBar tabs={tabs} active={tabActive} onChange={onTabChange} />
      ) : null}
      <div className="flex flex-col gap-0 lg:flex-row">
        <div className="min-w-0 flex-1 border-[var(--color-border)] p-4 sm:p-6 lg:border-r">{main}</div>
        {sidebar ? (
          <aside className="w-full shrink-0 border-t border-[var(--color-border)] bg-[var(--color-surface-2)] p-4 sm:p-5 lg:w-[320px] lg:border-l lg:border-t-0">
            {sidebar}
          </aside>
        ) : null}
      </div>
    </div>
  );
}

export function SfSidebarCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <h3 className="text-sm font-semibold text-[var(--color-text)]">{title}</h3>
      {description ? (
        <p className="mt-1 text-[11px] leading-snug text-[var(--color-text-muted)]">{description}</p>
      ) : null}
      <div className={cn(description ? "mt-3" : "mt-2")}>{children}</div>
    </div>
  );
}

/**
 * Compact row actions menu (Chevron).
 *
 * The popup is rendered through a React portal with `position: fixed` so it
 * cannot be clipped by an ancestor's `overflow: hidden` / `overflow-x: auto`
 * (the row menus live inside `SfTableWrap` + `SfListShell`, both of which
 * create scroll/clip contexts).
 */
export function SfRowMenu({
  children,
  ariaLabel = "Row actions",
}: {
  children: ReactNode;
  ariaLabel?: string;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const POPOVER_W = 200;
    const MARGIN = 8;
    let left = rect.right - POPOVER_W;
    if (left < MARGIN) left = MARGIN;
    if (left + POPOVER_W > window.innerWidth - MARGIN) {
      left = window.innerWidth - POPOVER_W - MARGIN;
    }
    let top = rect.bottom + 4;
    // Flip above the trigger when there isn't enough room below.
    if (top + 240 > window.innerHeight - MARGIN) {
      top = Math.max(MARGIN, rect.top - 4 - 240);
    }
    setPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node | null;
      if (!t) return;
      if (popoverRef.current?.contains(t)) return;
      if (triggerRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    function onReflow() {
      updatePosition();
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [open, updatePosition]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center rounded-md p-1.5 text-[var(--color-text-muted)] hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]"
      >
        <ChevronDown className="h-4 w-4" />
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={popoverRef}
              role="menu"
              className="fixed z-[100] min-w-[200px] rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] py-1"
              style={{
                top: pos.top,
                left: pos.left,
                boxShadow: "var(--shadow-modal, 0 10px 24px rgba(0,0,0,0.18))",
              }}
              onClick={() => setOpen(false)}
            >
              <div className="flex flex-col gap-0.5 px-1">{children}</div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

export function SfRowMenuBtn({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="block w-full rounded-md px-3 py-2 text-left text-xs text-[var(--color-text)] hover:bg-[var(--color-surface-2)] disabled:opacity-50"
    >
      {children}
    </button>
  );
}

export function SfTableWrap({ children }: { children: ReactNode }) {
  return <div className="overflow-x-auto">{children}</div>;
}
